import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import {
  TrendingUp as TrendingIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { StatusGlobal } from "src/interface/enum/StatusGlobal";

interface PerformanceSummaryProps {
  tasks?: any[];
  followups?: any[];
  agreements?: any[];
  transactions?: any[];
  services?: any[];
  isLoading?: boolean;
}

export default function PerformanceSummary({
  tasks = [],
  followups = [],
  agreements = [],
  transactions = [],
  services = [],
  isLoading = false,
}: PerformanceSummaryProps) {
  // محاسبه KPI های کلیدی
  const kpis = useMemo(() => {
    // اطمینان از اینکه همه داده‌ها آرایه هستند
    const safeTasks = Array.isArray(tasks) ? tasks : [];
    const safeFollowups = Array.isArray(followups) ? followups : [];
    const safeAgreements = Array.isArray(agreements) ? agreements : [];
    const safeTransactions = Array.isArray(transactions) ? transactions : [];

    // نرخ تکمیل کارها
    const completedTasks = safeTasks.filter(
      (t) => t.statusTask === StatusGlobal.Completed
    ).length;
    const taskCompletionRate =
      safeTasks.length > 0 ? (completedTasks / safeTasks.length) * 100 : 0;

    // نرخ تکمیل پیگیری‌ها
    const completedFollowups = safeFollowups.filter(
      (f) => f.scheduleStatus === StatusGlobal.Completed
    ).length;
    const followupCompletionRate =
      safeFollowups.length > 0
        ? (completedFollowups / safeFollowups.length) * 100
        : 0;

    // نرخ موفقیت قراردادها
    const activeAgreements = safeAgreements.filter(
      (a) => a.isActive !== false
    ).length;
    const agreementSuccessRate =
      safeAgreements.length > 0
        ? (activeAgreements / safeAgreements.length) * 100
        : 0;

    // نرخ پرداخت موفق
    const successfulPayments = safeTransactions.filter(
      (t) => t.transactionStatus === 1
    ).length;
    const paymentSuccessRate =
      safeTransactions.length > 0
        ? (successfulPayments / safeTransactions.length) * 100
        : 0;

    // میانگین زمان تکمیل کارها (روز)
    const completedTasksWithDates = safeTasks.filter(
      (t) =>
        t.statusTask === StatusGlobal.Completed && t.createdOn && t.modifiedOn
    );
    const avgTaskCompletionTime =
      completedTasksWithDates.length > 0
        ? completedTasksWithDates.reduce((sum, task) => {
            const created = new Date(task.createdOn);
            const modified = new Date(task.modifiedOn);
            const diffTime = modified.getTime() - created.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return sum + diffDays;
          }, 0) / completedTasksWithDates.length
        : 0;

    // کارهای فوری (تا 3 روز آینده)
    const urgentTasks = safeTasks.filter((task) => {
      if (!task.dueDate) return false;
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      const diffTime = dueDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return (
        diffDays <= 3 &&
        diffDays >= 0 &&
        task.statusTask !== StatusGlobal.Completed
      );
    });

    // کارهای تاخیر خورده
    const overdueTasks = safeTasks.filter((task) => {
      if (!task.dueDate) return false;
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      return dueDate < today && task.statusTask !== StatusGlobal.Completed;
    });

    // درآمد ماه جاری
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const currentMonthRevenue = safeTransactions
      .filter((t) => {
        if (!t.transactionDate || t.transactionStatus !== 1) return false;
        const transactionDate = new Date(t.transactionDate);
        return (
          transactionDate.getMonth() === currentMonth &&
          transactionDate.getFullYear() === currentYear
        );
      })
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // درآمد ماه قبل
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const lastMonthRevenue = safeTransactions
      .filter((t) => {
        if (!t.transactionDate || t.transactionStatus !== 1) return false;
        const transactionDate = new Date(t.transactionDate);
        return (
          transactionDate.getMonth() === lastMonth &&
          transactionDate.getFullYear() === lastMonthYear
        );
      })
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // درصد رشد درآمد
    const revenueGrowth =
      lastMonthRevenue > 0
        ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
        : 0;

    return {
      taskCompletionRate,
      followupCompletionRate,
      agreementSuccessRate,
      paymentSuccessRate,
      avgTaskCompletionTime,
      urgentTasks: urgentTasks.length,
      overdueTasks: overdueTasks.length,
      currentMonthRevenue,
      lastMonthRevenue,
      revenueGrowth,
    };
  }, [tasks, followups, agreements, transactions]);

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        خلاصه عملکرد
      </Typography>

      {/* KPI های اصلی */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "success.main", mx: "auto", mb: 2 }}>
                <CheckIcon />
              </Avatar>
              <Typography variant="h4" color="success.main">
                {kpis.taskCompletionRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                نرخ تکمیل کارها
              </Typography>
              <LinearProgress
                variant="determinate"
                value={kpis.taskCompletionRate}
                sx={{ mt: 1 }}
                color="success"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "info.main", mx: "auto", mb: 2 }}>
                <AssessmentIcon />
              </Avatar>
              <Typography variant="h4" color="info.main">
                {kpis.followupCompletionRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                نرخ تکمیل پیگیری‌ها
              </Typography>
              <LinearProgress
                variant="determinate"
                value={kpis.followupCompletionRate}
                sx={{ mt: 1 }}
                color="info"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "primary.main", mx: "auto", mb: 2 }}>
                <BusinessIcon />
              </Avatar>
              <Typography variant="h4" color="primary.main">
                {kpis.agreementSuccessRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                نرخ موفقیت قراردادها
              </Typography>
              <LinearProgress
                variant="determinate"
                value={kpis.agreementSuccessRate}
                sx={{ mt: 1 }}
                color="primary"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "secondary.main", mx: "auto", mb: 2 }}>
                <PaymentIcon />
              </Avatar>
              <Typography variant="h4" color="secondary.main">
                {kpis.paymentSuccessRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                نرخ پرداخت موفق
              </Typography>
              <LinearProgress
                variant="determinate"
                value={kpis.paymentSuccessRate}
                sx={{ mt: 1 }}
                color="secondary"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* آمار عملکرد */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              آمار عملکرد
            </Typography>
            <List dense>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <ScheduleIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="میانگین زمان تکمیل کارها"
                  secondary={`${kpis.avgTaskCompletionTime.toFixed(1)} روز`}
                />
              </ListItem>

              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "error.main" }}>
                    <WarningIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="کارهای فوری (تا 3 روز آینده)"
                  secondary={`${kpis.urgentTasks} کار`}
                />
              </ListItem>

              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "error.main" }}>
                    <ErrorIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="کارهای تاخیر خورده"
                  secondary={`${kpis.overdueTasks} کار`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              آمار مالی
            </Typography>
            <List dense>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "success.main" }}>
                    <TrendingIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="درآمد ماه جاری"
                  secondary={`${kpis.currentMonthRevenue.toLocaleString()} تومان`}
                />
              </ListItem>

              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "info.main" }}>
                    <AssessmentIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="درآمد ماه قبل"
                  secondary={`${kpis.lastMonthRevenue.toLocaleString()} تومان`}
                />
              </ListItem>

              <ListItem>
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor:
                        kpis.revenueGrowth >= 0 ? "success.main" : "error.main",
                    }}
                  >
                    {kpis.revenueGrowth >= 0 ? (
                      <TrendingIcon />
                    ) : (
                      <TrendingDownIcon />
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="رشد درآمد"
                  secondary={`${kpis.revenueGrowth >= 0 ? "+" : ""}${kpis.revenueGrowth.toFixed(1)}%`}
                />
                <Chip
                  label={kpis.revenueGrowth >= 0 ? "رشد مثبت" : "کاهش"}
                  color={kpis.revenueGrowth >= 0 ? "success" : "error"}
                  size="small"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* کارت‌های هشدار */}
      {(kpis.urgentTasks > 0 || kpis.overdueTasks > 0) && (
        <Paper sx={{ p: 3, bgcolor: "warning.light" }}>
          <Typography variant="h6" gutterBottom color="warning.dark">
            هشدارها
          </Typography>
          <Grid container spacing={2}>
            {kpis.urgentTasks > 0 && (
              <Grid xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="body2" color="warning.dark">
                    {kpis.urgentTasks} کار فوری نیاز به توجه دارد
                  </Typography>
                </Box>
              </Grid>
            )}
            {kpis.overdueTasks > 0 && (
              <Grid xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ErrorIcon color="error" />
                  <Typography variant="body2" color="error.dark">
                    {kpis.overdueTasks} کار تاخیر خورده وجود دارد
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
    </Box>
  );
}
